/**
 * quizGate.ts — where a page's questions are answered, and what they hold up.
 *
 * A page's questions are not *on* the page; they are **between it and the page
 * after it**. The reader reads page 3, leaves it, and the questions page 3 asked
 * are the door: answer them (or step over them), and page 4 is read. Written the
 * other way round — questions appearing as soon as you arrive — the reader is
 * asked about a page they have not read yet, which is the one thing a question
 * laid over a page must never do.
 *
 * So the *turn* is what is gated, and this file is the whole rule for it: no
 * renderer, no state, so the cases that matter (a page that asks something, a page
 * that does not, a page already answered, the last page, which has a door but no
 * room after it, and what the door *says* it will do) can be pinned down in tests
 * rather than discovered by turning pages in a feed.
 *
 * Nothing here decides *whether* a reader may skip: a question nobody can answer
 * must never trap somebody in a document, so stepping over is possible wherever
 * this is used. The rule is about what a turn *asks*, not about what it forbids.
 */

/** What stepping forward from a page should do. */
export type ForwardStep =
  | {
      /** The questions this step asks before letting the reader past. */
      askPage: number
    }
  | {
      /** The page to show: the next one, or absent at the end of the document. */
      toPage?: number
    }

/**
 * What a forward step from `from` does.
 *
 * Three answers, in the order they are asked:
 *
 *  - **The page asks something it has not been asked.** Ask it — `askPage` — and
 *    the reader stays where they are until the questions are done with. This is
 *    also the answer for the *last* page: leaving a document is a step forward
 *    too, and the questions of the page the reader is on are its door.
 *  - **Nothing to ask.** Move to the next page.
 *  - **Nothing to ask and nothing after.** The document ends here, and the caller
 *    falls back to its own forward behaviour (turning the last page again, or
 *    simply doing nothing).
 *
 * `questionsOn` is asked for a *count* rather than handed the material, so this
 * stays pure and does not care whether the questions came off a post's page table
 * or out of the companion record — a reader's turn must not wait on a fetch.
 */
export function forwardStep({
  from,
  totalPages,
  questionsOn,
  settled,
}: {
  /** The page the reader is on. */
  from: number
  totalPages: number
  /** How many questions a page holds. 0 is a page that asks nothing. */
  questionsOn: (page: number) => number
  /**
   * Whether that page's questions have been answered (or stepped over) in this
   * visit. Answered once is answered: a reader who turns back and forwards again
   * is not asked the same questions a second time.
   */
  settled: (page: number) => boolean
}): ForwardStep {
  if (questionsOn(from) > 0 && !settled(from)) {
    return {askPage: from}
  }
  return from < totalPages ? {toPage: from + 1} : {}
}

/**
 * The same door, for a reader who scrolls from one page to another.
 *
 * A turn is one page; a scroll can be fifteen, and the difference is only how many
 * pages it crosses — so this is `forwardStep` widened, not a second rule. The pages
 * it asks about are the ones being **left**: `from` itself, and every page a flick
 * skips over. The page the reader lands on is not among them, and never was: it has
 * not been read yet, and a question laid over a page nobody has seen is the one
 * thing a question over a page must not do.
 *
 *  - **A flick stopped mid-flight.** From page 1 to page 16, where page 2 asks, is
 *    a stop at page 2 — the reader is brought back to a page they have already seen
 *    rather than one they were heading for.
 *  - **A flick that crosses two pages that ask** stops at the first, and at the
 *    second when the reader is carried on: a caller that remembers where the flick
 *    was going asks again from the page after the one just settled.
 *  - **The end of the document** is a page being left like any other. A reader
 *    pushing at the bottom of the last page has nowhere to arrive, which is exactly
 *    the case `to` reports as one page past the end — and the last page's questions
 *    are then its door, the same door the paged reader's forward arrow opens.
 *
 * Backwards is never held up: a flick *up* the document is not a step forward, so
 * the loop has nothing to walk and the reader arrives.
 */
export function scrollStep({
  from,
  to,
  totalPages,
  questionsOn,
  settled,
}: {
  /** The page the reader is scrolling away from. */
  from: number
  /**
   * The page they have landed on — or, when they pushed against the document's own
   * end, one past the last page, which is how "leaving the last page" is said.
   */
  to: number
  totalPages: number
  questionsOn: (page: number) => number
  settled: (page: number) => boolean
}): ForwardStep {
  for (let page = from; page < to; page++) {
    if (questionsOn(page) > 0 && !settled(page)) {
      return {askPage: page}
    }
  }
  return to <= totalPages ? {toPage: to} : {}
}

/**
 * The same set of pages with one marked settled — the identical set if it already is.
 *
 * What the reader remembers as *done with* is state (`PdfFullscreen`), because the
 * forward door is decided from it while rendering and a set that decides what is drawn
 * cannot be a ref. That makes every write a new value, and this is what keeps the new
 * value genuinely new: a page settled twice would otherwise re-render a reader that
 * nothing has changed for.
 *
 * It lives here, beside the rule that consumes the set, rather than inline in the
 * reader — which writes it from two places (the questions closing, and a page whose
 * material turns out to ask nothing) and would have the copy idiom in two shapes to
 * drift apart.
 */
export function withQuizSettled(
  settled: ReadonlySet<number>,
  page: number,
): ReadonlySet<number> {
  return settled.has(page) ? settled : new Set(settled).add(page)
}

/**
 * The page a reader is carried on to when they finish the questions in front of them.
 *
 * Usually the page next door — but not always: a flick, a jump from the thumbnails or
 * **End** can cross several pages to reach the questions being answered, and where the
 * reader was going is remembered (`from` below). The card on screen names *that* page,
 * because a card promising the next page while the door opens a farther one is a
 * promise the door breaks on its way through.
 *
 * `undefined` means there is nowhere to be carried on to: the reader is on the last
 * page (or was pushing past it), and the questions in front of them are the way out of
 * the document rather than a door between two of its pages.
 */
export function carryOnPage({
  quizPage,
  pendingPage,
  totalPages,
}: {
  /** The page whose questions are on screen, or none. */
  quizPage: number | null
  /** Where the turn that opened them was going, if it was crossing something. */
  pendingPage: number | null
  totalPages: number
}): number | undefined {
  if (quizPage === null) return undefined
  const wanted =
    pendingPage !== null && pendingPage > quizPage ? pendingPage : quizPage + 1
  const landing = Math.min(wanted, totalPages)
  return landing > quizPage ? landing : undefined
}
